/*
 * Keeöz — ajout panier global.
 * - KeeozCart.add(items, { button }) : ajout via cart/add.js, re-rendu du tiroir Eurus, ouverture du tiroir.
 * - addCollectionProductToCart : repli global pour les cartes produit hors grille collection (recherche, recommandations…).
 * - Boutons « Choisissez les options » : renvoi vers la fiche quand l'aperçu rapide est désactivé.
 * - Modules upsell panier et routine fiche produit [data-kz-routine].
 * - Tracking : chaque ajout depuis un module Keeöz porte la propriété de ligne cachée `_kz_upsell`
 *   (visible sur la commande dans l'admin, pas côté client) et publie l'événement `keeoz_upsell_add`
 *   (Shopify.analytics → pixels personnalisés, et dataLayer si présent).
 */
(() => {
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const store = (name) => (window.Alpine && window.Alpine.store(name)) || null;

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency }).format(cents / 100);
  };

  const errorFor = (button) => {
    if (!button) return null;
    const scope = button.closest('[data-kz-scope]');
    let el = scope ? scope.querySelector('[data-kz-error]') : null;
    if (!el) {
      const anchor = button.closest('form') || button;
      el = anchor.parentElement && anchor.parentElement.querySelector(':scope > .kz-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'kz-error';
        el.setAttribute('role', 'alert');
        anchor.insertAdjacentElement('afterend', el);
      }
    }
    return el;
  };

  const showError = (button, message) => {
    const el = errorFor(button);
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  const clearError = (button) => {
    const el = errorFor(button);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  };

  const SOURCE_PROPERTY = '_kz_upsell';

  const sourceOf = (button) => {
    const scope = button && button.closest('[data-kz-source]');
    return scope ? scope.dataset.kzSource : null;
  };

  const track = (source, items, data) => {
    const payload = {
      source,
      page_type: (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.pageType) || null,
      items: ((data && data.items) || items).map((item) => ({
        variant_id: item.variant_id || item.id,
        product_id: item.product_id || null,
        title: item.product_title || null,
        price: typeof item.final_price === 'number' ? item.final_price / 100 : null,
        quantity: item.quantity,
      })),
    };
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('keeoz_upsell_add', payload);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'keeoz_upsell_add', keeoz_upsell: payload });
      }
    } catch (error) {
      console.error('Keeoz tracking:', error);
    }
  };

  async function add(items, { button, source = sourceOf(button) } = {}) {
    if (source) {
      items = items.map((item) => ({ ...item, properties: { ...(item.properties || {}), [SOURCE_PROPERTY]: source } }));
    }
    const helper = store('xCartHelper');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      clearError(button);
    }

    try {
      if (helper && helper.waitForCartUpdate) await helper.waitForCartUpdate();
      window.updatingCart = true;

      const sections = helper ? helper.getSectionsToRender().map((section) => section.id) : [];
      const response = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Oups, impossible d’ajouter ce produit.');
      }

      if (source) track(source, items, data);

      // Page panier : pas de tiroir à ouvrir, on recharge pour refléter le panier.
      if (button && button.closest('[data-kz-reload]')) {
        window.location.reload();
        return data;
      }

      if (helper && data.sections) await helper.reRenderSections(data.sections);

      const quickView = store('xQuickView');
      if (quickView && quickView.show) quickView.show = false;

      const noti = store('xCartNoti');
      if (noti && noti.enable) {
        noti.setItem(data);
      } else if (store('xMiniCart')) {
        store('xMiniCart').openCart();
        document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
      } else {
        window.location.href = root() + 'cart';
      }

      const bubble = document.querySelector('#cart-icon-bubble span');
      if (helper && bubble) helper.currentItemCount = parseInt(bubble.innerHTML, 10) || 0;
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

      return data;
    } catch (error) {
      console.error('Keeoz add to cart:', error);
      showError(button, error.message);
      return null;
    } finally {
      window.updatingCart = false;
      if (button && button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  window.KeeozCart = { add, formatMoney, track, SOURCE_PROPERTY };

  // La grille collection déclare sa propre fonction en ligne : on ne la remplace pas.
  if (typeof window.addCollectionProductToCart !== 'function') {
    window.addCollectionProductToCart = (button) => {
      const form = button.closest('form');
      const variantInput = form && form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        console.error('Keeoz: variant not found for quick add');
        return;
      }
      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value || '1', 10)) : 1;
      return add([{ id: variantInput.value.trim(), quantity }], { button });
    };
  }

  // « Choisissez les options » sans aperçu rapide actif : on ouvre la fiche produit.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.card-product button.button--quickview[name="add"]');
      if (!button) return;
      const quickView = store('xQuickView');
      if (quickView && quickView.enabled) return;
      const link = button.closest('.card-product').querySelector('a[href*="/products/"]');
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = link.href;
    },
    true
  );

  // Ajout d'un produit depuis le module upsell panier.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-kz-upsell-add]');
    if (!button) return;
    event.preventDefault();
    const card = button.closest('[data-kz-card]');
    const select = card && card.querySelector('select[name="id"]');
    const id = select ? select.value : button.dataset.variantId;
    if (id) add([{ id, quantity: 1 }], { button });
  });

  /* ---------- Recommandations Shopify (intent=related), mises en cache ---------- */

  const relatedCache = new Map();

  const fetchRelated = (productId, limit) => {
    const key = `${productId}:${limit}`;
    if (!relatedCache.has(key)) {
      relatedCache.set(
        key,
        fetch(`${root()}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`)
          .then((response) => (response.ok ? response.json() : { products: [] }))
          .then((data) => data.products || [])
          .catch(() => [])
      );
    }
    return relatedCache.get(key);
  };

  const escapeHTML = (value) =>
    String(value || '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const imageURL = (src, width) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '');

  /* ---------- Upsell panier : recommandations Shopify si aucun complémentaire S&D ---------- */

  const upsellCardHTML = (product, { badge, source, badgeText, buttonText }) => {
    const variants = product.variants.filter((variant) => variant.available);
    const first = variants[0];
    const title = escapeHTML(product.title);
    const image = imageURL(product.featured_image, 120);
    const priceOrSelect =
      variants.length > 1
        ? `<select name="id" class="kz-upsell__variant" aria-label="${title}">${variants
            .map((v) => `<option value="${v.id}">${escapeHTML(v.title)} – ${formatMoney(v.price)}</option>`)
            .join('')}</select>`
        : `<span class="kz-upsell__price">${formatMoney(first.price)}${
            badge ? ` <span class="kz-upsell__badge">${escapeHTML(badgeText)}</span>` : ''
          }</span>`;
    return `<li class="kz-upsell__card" data-kz-card data-kz-scope data-kz-source="${escapeHTML(source)}">
      <a href="${escapeHTML(product.url)}" class="kz-upsell__media" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${image}" alt="${title}" width="56" height="56" loading="lazy">` : ''}
      </a>
      <div class="kz-upsell__info">
        <a href="${escapeHTML(product.url)}" class="kz-upsell__name">${title}</a>
        ${priceOrSelect}
      </div>
      <button type="button" class="kz-upsell__button" data-kz-upsell-add data-variant-id="${first.id}" aria-label="${escapeHTML(buttonText)} – ${title}">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>
      </button>
      <p class="kz-error" data-kz-error role="alert" hidden></p>
    </li>`;
  };

  const upsellAIInit = async (section) => {
    if (section.dataset.kzAiReady) return;
    section.dataset.kzAiReady = 'true';
    const list = section.querySelector('.kz-upsell__list');
    const limit = parseInt(section.dataset.kzLimit || '2', 10);
    const remaining = parseInt(section.dataset.kzRemaining || '0', 10);
    const exclude = section.dataset.kzExclude || '|';

    const products = (await fetchRelated(section.dataset.kzAi, limit + 4))
      .filter((product) => product.available && !exclude.includes(`|${product.id}|`))
      .filter((product) => product.variants.some((variant) => variant.available))
      .slice(0, limit);

    if (products.length && section.isConnected) {
      const priceOf = (product) => product.variants.find((variant) => variant.available).price;
      const unlocks = (product) => remaining > 0 && priceOf(product) >= remaining;
      const options = {
        source: section.dataset.kzAiSource,
        badgeText: section.dataset.kzBadge,
        buttonText: section.dataset.kzButton,
      };
      list.innerHTML = [...products.filter(unlocks), ...products.filter((product) => !unlocks(product))]
        .map((product) => upsellCardHTML(product, { ...options, badge: unlocks(product) }))
        .join('');
    }

    // Recommandations ou, à défaut, liste de secours déjà rendue côté serveur.
    if (list.children.length) section.hidden = false;
    if (typeof sliderUpdate === 'function') sliderUpdate(list);
  };

  /* ---------- Slider du module upsell panier ---------- */

  const sliderUpdate = (list) => {
    const nav = list.closest('.kz-upsell') && list.closest('.kz-upsell').querySelector('[data-kz-nav]');
    if (!nav) return;
    const max = list.scrollWidth - list.clientWidth;
    nav.hidden = list.clientWidth === 0 || max <= 2;
    nav.querySelector('[data-kz-prev]').disabled = list.scrollLeft <= 2;
    nav.querySelector('[data-kz-next]').disabled = list.scrollLeft >= max - 2;
  };

  const sliderObserver = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((entry) => sliderUpdate(entry.target))) : null;

  const sliderInit = (scope) => {
    scope.querySelectorAll('.kz-upsell__list:not([data-kz-slider])').forEach((list) => {
      list.dataset.kzSlider = 'true';
      list.addEventListener('scroll', () => sliderUpdate(list), { passive: true });
      if (sliderObserver) sliderObserver.observe(list);
      sliderUpdate(list);
    });
  };

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-kz-prev], [data-kz-next]');
    if (!arrow) return;
    const list = arrow.closest('.kz-upsell').querySelector('.kz-upsell__list');
    const card = list && list.querySelector('.kz-upsell__card');
    if (!card) return;
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(list).columnGap || '8');
    list.scrollBy({ left: arrow.hasAttribute('data-kz-prev') ? -step : step });
  });

  // Le tiroir est re-rendu à chaque changement de panier : on initialise les nouveaux sliders.
  let sliderQueued = false;
  new MutationObserver(() => {
    if (sliderQueued) return;
    sliderQueued = true;
    requestAnimationFrame(() => {
      sliderQueued = false;
      document.querySelectorAll('[data-kz-ai]:not([data-kz-ai-ready])').forEach(upsellAIInit);
      sliderInit(document);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---------- Bloc « Ta routine » (fiche produit) ---------- */

  const routineUpdate = (block) => {
    const rows = [...block.querySelectorAll('[data-kz-row]')];
    let total = 0;
    let count = 0;
    rows.forEach((row) => {
      const checkbox = row.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        const select = row.querySelector('select');
        const option = select ? select.selectedOptions[0] : null;
        total += parseInt((option && option.dataset.price) || row.dataset.price || '0', 10);
        count += 1;
      }
    });
    const totalEl = block.querySelector('[data-kz-total]');
    const countEl = block.querySelector('[data-kz-count]');
    const button = block.querySelector('[data-kz-routine-add]');
    if (totalEl) totalEl.textContent = formatMoney(total);
    if (countEl) countEl.textContent = count;
    if (button) button.disabled = count === 0;
  };

  const routineRowHTML = (product, index, checked) => {
    const title = escapeHTML(product.title);
    const variants = product.variants.filter((variant) => variant.available);
    if (!variants.length) return '';
    const first = variants[0];
    const image = imageURL(product.featured_image, 120);
    const url = escapeHTML(product.url || `/products/${product.handle}`);
    const select =
      variants.length > 1
        ? `<select class="kz-routine__variant" aria-label="Option pour ${title}">${variants
            .map((v) => `<option value="${v.id}" data-price="${v.price}">${escapeHTML(v.title)}</option>`)
            .join('')}</select>`
        : '';
    return `<li class="kz-routine__row" data-kz-row data-variant-id="${first.id}" data-price="${first.price}">
      <input type="checkbox" class="kz-routine__check" id="kz-routine-js-${index}"${checked ? ' checked' : ''}>
      <label class="kz-routine__label" for="kz-routine-js-${index}">
        ${image ? `<a class="kz-routine__media" href="${url}" tabindex="-1" aria-hidden="true"><img class="kz-routine__img" src="${image}" alt="${title}" width="56" height="56" loading="lazy"></a>` : ''}
        <span class="kz-routine__info">
          <span class="kz-routine__vendor">${escapeHTML(product.vendor)}</span>
          <a class="kz-routine__name" href="${url}">${title}</a>
        </span>
        <span class="kz-routine__price">${formatMoney(first.price)}</span>
      </label>
      ${select}
    </li>`;
  };

  const routineFallback = async (block) => {
    const list = block.querySelector('[data-kz-list]');
    const productId = block.dataset.productId;
    const limit = parseInt(block.dataset.limit || '2', 10);
    const checked = block.dataset.preselect === 'true';
    const html = (await fetchRelated(productId, limit + 2))
      .filter((product) => product.available && String(product.id) !== productId)
      .slice(0, limit)
      .map((product, index) => routineRowHTML(product, index, checked))
      .join('');
    if (!html || !block.isConnected) return;
    list.innerHTML = html;
    block.dataset.kzSource = 'fiche_ia';
    block.hidden = false;
    routineUpdate(block);
  };

  const routineInit = (block) => {
    if (block.dataset.kzReady) return;
    block.dataset.kzReady = 'true';

    block.addEventListener('change', (event) => {
      const select = event.target.closest('select');
      if (select) {
        const row = select.closest('[data-kz-row]');
        row.dataset.variantId = select.value;
        const priceEl = row.querySelector('.kz-routine__price');
        if (priceEl) priceEl.textContent = formatMoney(parseInt(select.selectedOptions[0].dataset.price, 10));
      }
      routineUpdate(block);
    });

    block.addEventListener('click', (event) => {
      const button = event.target.closest('[data-kz-routine-add]');
      if (!button) return;
      const items = [];
      block.querySelectorAll('[data-kz-row]').forEach((row) => {
        const checkbox = row.querySelector('input[type="checkbox"]');
        if (checkbox && checkbox.checked) items.push({ id: row.dataset.variantId, quantity: 1 });
      });
      if (items.length) add(items, { button });
    });

    if (block.dataset.fallback === 'true') {
      routineFallback(block);
    } else {
      routineUpdate(block);
    }
  };

  const initAll = () => {
    document.querySelectorAll('[data-kz-routine]').forEach(routineInit);
    document.querySelectorAll('[data-kz-ai]:not([data-kz-ai-ready])').forEach(upsellAIInit);
    sliderInit(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
